import React from "react";
import firebase from "firebase/compat/app";
import {
    FiBookOpen,
    FiChevronDown,
    FiChevronRight,
    FiClock,
    FiCompass,
    FiCpu,
    FiLock,
    FiLogOut,
    FiPlayCircle,
    FiTarget,
    FiTrash2,
    FiUserPlus,
    FiZap,
} from "react-icons/fi";
import { aboutContent } from "../../data/portfolioContent";
import { auth, firestore } from "../../configs/firebase";
import { localized, pick } from "../../utils/localization";
import PageFooter from "./PageFooter";

const ENDPOINT = process.env.REACT_APP_CHAT_ENDPOINT || "";
const ROADMAP_COLLECTION = "learningPaths";

const copy = {
    title: localized("AI Learning Path", "Lộ trình học AI"),
    description: localized(
        "Describe what you want to learn — the AI mentor drafts a step-by-step roadmap with video & article links, saved to your account.",
        "Nhập kiến thức bạn muốn học — AI sẽ vạch lộ trình từng bước kèm link video & tài liệu, lưu vào tài khoản của bạn."
    ),
    goalLabel: localized("What do you want to learn?", "Bạn muốn học gì?"),
    goalPlaceholder: localized(
        "e.g. Backend development with Laravel, or System Design for interviews",
        "vd: Lập trình backend với Laravel, hoặc System Design để phỏng vấn"
    ),
    levelLabel: localized("Current level", "Trình độ hiện tại"),
    levels: [
        { id: "beginner", label: localized("Beginner", "Mới bắt đầu") },
        { id: "intermediate", label: localized("Intermediate", "Trung cấp") },
        { id: "advanced", label: localized("Advanced", "Nâng cao") },
    ],
    timeLabel: localized("Time you can commit", "Thời gian bạn có thể dành"),
    times: [
        { id: "casual", label: localized("A few hours / week", "Vài giờ / tuần") },
        { id: "steady", label: localized("~1 hour / day", "~1 giờ / ngày") },
        { id: "intense", label: localized("Full-time / bootcamp", "Toàn thời gian / bootcamp") },
    ],
    notesLabel: localized("Anything else? (optional)", "Điều gì khác? (tùy chọn)"),
    notesPlaceholder: localized(
        "e.g. I already know PHP basics; I prefer project-based learning",
        "vd: Đã biết PHP cơ bản; thích học qua dự án thực tế"
    ),
    submit: localized("Generate roadmap", "Tạo lộ trình"),
    generating: localized("AI is planning your roadmap…", "AI đang lên lộ trình cho bạn…"),
    summaryLabel: localized("Overview", "Tổng quan"),
    phaseLabel: localized("Phase", "Giai đoạn"),
    videoLabel: localized("Videos", "Video"),
    docsLabel: localized("Articles", "Tài liệu"),
    savedTitle: localized("My roadmaps", "Lộ trình của tôi"),
    savedEmpty: localized("Roadmaps you generate are saved to your account and shown here.", "Lộ trình bạn tạo sẽ được lưu vào tài khoản và hiển thị ở đây."),
    savedCount: (count) => localized(`${count} phase${count === 1 ? "" : "s"}`, `${count} giai đoạn`),
    justNow: localized("Just now", "Vừa xong"),
    delete: localized("Delete", "Xóa"),
    emptyGoal: localized("Please enter what you want to learn.", "Hãy nhập kiến thức bạn muốn học."),
    noEndpoint: localized(
        "The AI service is not configured yet. Please try again later.",
        "Dịch vụ AI chưa được cấu hình. Vui lòng thử lại sau."
    ),
    error: localized(
        "Sorry, the AI mentor is busy. Please try again in a moment.",
        "Xin lỗi, AI đang bận. Bạn thử lại sau giây lát nhé."
    ),
    footer: localized("Powered by AI — a starting point, not a rulebook.", "Được tạo bởi AI — là điểm khởi đầu, không phải khuôn mẫu cứng."),
    // Auth
    authTitle: localized("Sign in to build your roadmap", "Đăng nhập để tạo lộ trình"),
    authHint: localized(
        "Create a free account so your roadmaps are saved to you and available on any device.",
        "Tạo tài khoản miễn phí để lộ trình được lưu theo bạn và xem được trên mọi thiết bị."
    ),
    email: localized("Email", "Email"),
    password: localized("Password (min 6 characters)", "Mật khẩu (tối thiểu 6 ký tự)"),
    displayName: localized("Display name (optional)", "Tên hiển thị (tùy chọn)"),
    login: localized("Sign in", "Đăng nhập"),
    register: localized("Create account", "Tạo tài khoản"),
    switchToRegister: localized("No account? Create one", "Chưa có tài khoản? Tạo mới"),
    switchToLogin: localized("Already have an account? Sign in", "Đã có tài khoản? Đăng nhập"),
    logout: localized("Sign out", "Đăng xuất"),
    loggedInAs: localized("Signed in as", "Đăng nhập với"),
    loginRequired: localized("Please sign in to generate a roadmap.", "Vui lòng đăng nhập để tạo lộ trình."),
};

const toMillis = (value) => {
    if (!value) return 0;
    if (typeof value.toMillis === "function") return value.toMillis();
    if (typeof value.toDate === "function") return value.toDate().getTime();
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

// Parse output theo format cố định của Worker:
//   SUMMARY: ...
//   PHASE: <title> | <duration> | <focus>
//   - step
//   SEARCH: <keywords>
const parseRoadmap = (text) => {
    const lines = String(text || "").replace(/\r/g, "").split("\n");
    let summary = "";
    const phases = [];
    let current = null;

    for (const raw of lines) {
        const line = raw.trim();
        if (!line) continue;

        const sum = /^SUMMARY:\s*(.*)$/i.exec(line);
        if (sum) {
            summary = sum[1].trim();
            continue;
        }

        const phase = /^PHASE:\s*(.*)$/i.exec(line);
        if (phase) {
            const parts = phase[1].split("|").map((part) => part.trim());
            current = {
                title: parts[0] || "",
                duration: parts[1] || "",
                focus: parts[2] || "",
                steps: [],
                search: "",
            };
            phases.push(current);
            continue;
        }

        const search = /^SEARCH:\s*(.*)$/i.exec(line);
        if (search && current) {
            current.search = search[1].trim();
            continue;
        }

        const step = /^[-*•]\s+(.*)$/.exec(line);
        if (step && current) {
            current.steps.push(step[1].trim());
        }
    }

    return { summary, phases };
};

// Link học đáng tin cậy: dựng từ SEARCH keywords → luôn hoạt động, đúng chủ đề.
const buildResources = (phase, language) => {
    const query = (phase.search || phase.title || phase.focus || "").trim();
    if (!query) return [];

    const encoded = encodeURIComponent(`${query} tutorial`);

    return [
        {
            id: "video",
            label: pick(copy.videoLabel, language),
            icon: "video",
            url: `https://www.youtube.com/results?search_query=${encoded}`,
        },
        {
            id: "docs",
            label: pick(copy.docsLabel, language),
            icon: "docs",
            url: `https://www.google.com/search?q=${encoded}`,
        },
    ];
};

// Nội dung 1 lộ trình (tóm tắt + timeline các giai đoạn), tái dùng cho mọi thẻ.
const RoadmapBody = ({ roadmap, rawReply, language }) => {
    const t = (entry) => pick(entry, language);
    const hasPhases = roadmap?.phases?.length > 0;

    return (
        <div className="portfolio-learn-roadmap">
            {roadmap?.summary && (
                <div className="portfolio-learn-summary">
                    <strong>{t(copy.summaryLabel)}</strong>
                    <p>{roadmap.summary}</p>
                </div>
            )}

            {hasPhases ? (
                <ol className="portfolio-learn-timeline">
                    {roadmap.phases.map((phase, index) => {
                        const resources = buildResources(phase, language);

                        return (
                            <li className="portfolio-learn-phase" key={`${phase.title}-${index}`}>
                                <div className="portfolio-learn-phase-marker">
                                    {String(index + 1).padStart(2, "0")}
                                </div>
                                <div className="portfolio-learn-phase-body">
                                    <div className="portfolio-learn-phase-head">
                                        <h3>{phase.title || `${t(copy.phaseLabel)} ${index + 1}`}</h3>
                                        {phase.duration && (
                                            <span className="portfolio-learn-phase-duration">
                                                <FiClock />
                                                {phase.duration}
                                            </span>
                                        )}
                                    </div>
                                    {phase.focus && (
                                        <p className="portfolio-learn-phase-focus">{phase.focus}</p>
                                    )}
                                    {phase.steps?.length > 0 && (
                                        <ul>
                                            {phase.steps.map((step, stepIndex) => (
                                                <li key={stepIndex}>{step}</li>
                                            ))}
                                        </ul>
                                    )}
                                    {resources.length > 0 && (
                                        <div className="portfolio-learn-phase-links">
                                            {resources.map((resource) => (
                                                <a
                                                    key={resource.id}
                                                    className={`portfolio-learn-res is-${resource.id}`}
                                                    href={resource.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    {resource.icon === "video" ? <FiPlayCircle /> : <FiBookOpen />}
                                                    {resource.label}
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ol>
            ) : (
                <div className="portfolio-learn-raw">
                    {String(rawReply || "").split(/\n{2,}/).map((block, index) => (
                        <p key={index}>{block}</p>
                    ))}
                </div>
            )}
        </div>
    );
};

const LearningPath = ({ language }) => {
    const [goal, setGoal] = React.useState("");
    const [level, setLevel] = React.useState("beginner");
    const [time, setTime] = React.useState("steady");
    const [notes, setNotes] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState("");
    const [saved, setSaved] = React.useState([]);
    const [collapsed, setCollapsed] = React.useState({});
    const savedRef = React.useRef(null);

    // Auth state
    const [user, setUser] = React.useState(() => auth.currentUser);
    const [authMode, setAuthMode] = React.useState("login");
    const [authEmail, setAuthEmail] = React.useState("");
    const [authPassword, setAuthPassword] = React.useState("");
    const [displayName, setDisplayName] = React.useState("");
    const [authError, setAuthError] = React.useState("");
    const [savingAuth, setSavingAuth] = React.useState(false);

    const t = (entry) => pick(entry, language);

    React.useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((currentUser) => setUser(currentUser));
        return () => unsubscribe();
    }, []);

    // Danh sách lộ trình của riêng user (Firestore, lọc theo uid, sort client-side
    // để không cần composite index). Bản ghi vừa tạo (createdAt pending) đẩy lên đầu.
    React.useEffect(() => {
        if (!user) {
            setSaved([]);
            return undefined;
        }

        const unsubscribe = firestore
            .collection(ROADMAP_COLLECTION)
            .where("uid", "==", user.uid)
            .onSnapshot(
                (snapshot) => {
                    const items = snapshot.docs
                        .map((doc) => ({ id: doc.id, ...doc.data() }))
                        .sort((a, b) => (toMillis(b.createdAt) || Number.MAX_SAFE_INTEGER) - (toMillis(a.createdAt) || Number.MAX_SAFE_INTEGER));
                    setSaved(items);
                },
                (snapshotError) => {
                    console.error("[learning-path] snapshot error:", snapshotError.message);
                }
            );

        return () => unsubscribe();
    }, [user]);

    const toggleCard = (id) => {
        setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleAuth = async (event) => {
        event.preventDefault();
        setAuthError("");
        setSavingAuth(true);

        try {
            if (authMode === "register") {
                const credential = await auth.createUserWithEmailAndPassword(authEmail, authPassword);

                if (displayName.trim()) {
                    await credential.user.updateProfile({ displayName: displayName.trim() });
                }
            } else {
                await auth.signInWithEmailAndPassword(authEmail, authPassword);
            }

            setAuthEmail("");
            setAuthPassword("");
            setDisplayName("");
        } catch (authException) {
            setAuthError(authException.message);
        } finally {
            setSavingAuth(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!user) {
            setError(t(copy.loginRequired));
            return;
        }

        const trimmedGoal = goal.trim();
        if (!trimmedGoal) {
            setError(t(copy.emptyGoal));
            return;
        }
        if (!ENDPOINT) {
            setError(t(copy.noEndpoint));
            return;
        }

        setError("");
        setLoading(true);

        const levelLabel = t(copy.levels.find((item) => item.id === level)?.label || copy.levels[0].label);
        const timeLabel = t(copy.times.find((item) => item.id === time)?.label || copy.times[0].label);
        const message = [
            `Goal / topic to learn: ${trimmedGoal}.`,
            `Current level: ${levelLabel}.`,
            `Time available: ${timeLabel}.`,
            notes.trim() ? `Extra notes: ${notes.trim()}.` : "",
        ]
            .filter(Boolean)
            .join(" ");

        try {
            const res = await fetch(ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ message, lang: language, mode: "roadmap" }),
            });
            const data = await res.json().catch(() => ({}));

            if (!res.ok) {
                setError(data?.error?.toString().trim() || t(copy.error));
                return;
            }

            const reply = data?.reply?.toString().trim() || "";
            const roadmap = parseRoadmap(reply);

            // Lưu vào Firestore theo user; onSnapshot tự đẩy thẻ mới lên đầu danh sách.
            await firestore.collection(ROADMAP_COLLECTION).add({
                uid: user.uid,
                userEmail: user.email || "",
                goal: trimmedGoal,
                summary: roadmap.summary,
                phases: roadmap.phases,
                rawReply: reply,
                createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            });

            window.requestAnimationFrame(() => {
                savedRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            });
        } catch (requestError) {
            console.error("[learning-path] request failed:", requestError?.message);
            setError(t(copy.error));
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSaved = async (id) => {
        try {
            await firestore.collection(ROADMAP_COLLECTION).doc(id).delete();
        } catch (deleteError) {
            console.error("[learning-path] delete error:", deleteError.message);
        }
    };

    const dateFmt = language === "vi" ? "vi-VN" : "en-US";

    return (
        <div className="portfolio-page">
            <section className="portfolio-learn-wrap">
                <div className="portfolio-learn-header">
                    <div className="portfolio-learn-badge">
                        <FiCpu />
                        <span>AI</span>
                    </div>
                    <h1>{t(copy.title)}</h1>
                    <p>{t(copy.description)}</p>
                </div>

                {!user ? (
                    <div className="portfolio-learn-auth">
                        <div className="portfolio-learn-auth-icon" aria-hidden="true">
                            <FiLock />
                        </div>
                        <h2>{t(copy.authTitle)}</h2>
                        <p>{t(copy.authHint)}</p>

                        <form className="portfolio-learn-auth-form" onSubmit={handleAuth}>
                            {authMode === "register" && (
                                <input
                                    value={displayName}
                                    onChange={(event) => setDisplayName(event.target.value)}
                                    placeholder={t(copy.displayName)}
                                    maxLength={120}
                                />
                            )}
                            <input
                                type="email"
                                value={authEmail}
                                onChange={(event) => setAuthEmail(event.target.value)}
                                placeholder={t(copy.email)}
                                maxLength={254}
                                required
                            />
                            <input
                                type="password"
                                value={authPassword}
                                onChange={(event) => setAuthPassword(event.target.value)}
                                placeholder={t(copy.password)}
                                minLength={6}
                                required
                            />

                            <button
                                type="submit"
                                className="portfolio-btn portfolio-btn-primary"
                                disabled={savingAuth}
                            >
                                <FiUserPlus />
                                {savingAuth ? "..." : authMode === "register" ? t(copy.register) : t(copy.login)}
                            </button>

                            <button
                                type="button"
                                className="portfolio-learn-auth-switch"
                                onClick={() => {
                                    setAuthError("");
                                    setAuthMode((current) => (current === "login" ? "register" : "login"));
                                }}
                            >
                                {authMode === "login" ? t(copy.switchToRegister) : t(copy.switchToLogin)}
                            </button>
                        </form>

                        {authError && <div className="portfolio-learn-state is-error">{authError}</div>}
                    </div>
                ) : (
                    <>
                        <div className="portfolio-learn-userbar">
                            <span>
                                {t(copy.loggedInAs)} <strong>{user.displayName || user.email}</strong>
                            </span>
                            <button type="button" onClick={() => auth.signOut()}>
                                <FiLogOut />
                                {t(copy.logout)}
                            </button>
                        </div>

                        <form className="portfolio-learn-form" onSubmit={handleSubmit}>
                            <label className="portfolio-learn-field portfolio-learn-field-wide">
                                <span>
                                    <FiTarget />
                                    {t(copy.goalLabel)}
                                </span>
                                <input
                                    value={goal}
                                    onChange={(event) => setGoal(event.target.value)}
                                    placeholder={t(copy.goalPlaceholder)}
                                    maxLength={200}
                                />
                            </label>

                            <label className="portfolio-learn-field">
                                <span>
                                    <FiCompass />
                                    {t(copy.levelLabel)}
                                </span>
                                <select value={level} onChange={(event) => setLevel(event.target.value)}>
                                    {copy.levels.map((item) => (
                                        <option key={item.id} value={item.id}>
                                            {t(item.label)}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label className="portfolio-learn-field">
                                <span>
                                    <FiClock />
                                    {t(copy.timeLabel)}
                                </span>
                                <select value={time} onChange={(event) => setTime(event.target.value)}>
                                    {copy.times.map((item) => (
                                        <option key={item.id} value={item.id}>
                                            {t(item.label)}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label className="portfolio-learn-field portfolio-learn-field-wide">
                                <span>{t(copy.notesLabel)}</span>
                                <input
                                    value={notes}
                                    onChange={(event) => setNotes(event.target.value)}
                                    placeholder={t(copy.notesPlaceholder)}
                                    maxLength={300}
                                />
                            </label>

                            <div className="portfolio-learn-actions">
                                <button
                                    type="submit"
                                    className="portfolio-btn portfolio-btn-primary"
                                    disabled={loading}
                                >
                                    <FiZap />
                                    {loading ? t(copy.generating) : t(copy.submit)}
                                </button>
                            </div>
                        </form>

                        {error && <div className="portfolio-learn-state is-error">{error}</div>}

                        {loading && (
                            <div className="portfolio-learn-loading" aria-live="polite">
                                <span className="portfolio-learn-spinner" aria-hidden="true"></span>
                                {t(copy.generating)}
                            </div>
                        )}

                        <div className="portfolio-learn-saved" ref={savedRef}>
                            <div className="portfolio-learn-saved-head">
                                <FiBookOpen />
                                <span>{t(copy.savedTitle)}</span>
                            </div>

                            {saved.length === 0 ? (
                                <p className="portfolio-learn-saved-empty">{t(copy.savedEmpty)}</p>
                            ) : (
                                <div className="portfolio-learn-card-list">
                                    {saved.map((record) => {
                                        const isCollapsed = Boolean(collapsed[record.id]);
                                        const dateText = record.createdAt
                                            ? new Date(toMillis(record.createdAt)).toLocaleDateString(dateFmt, {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                            })
                                            : t(copy.justNow);

                                        return (
                                            <article className="portfolio-learn-card" key={record.id}>
                                                <div className="portfolio-learn-card-head">
                                                    <button
                                                        type="button"
                                                        className="portfolio-learn-card-toggle"
                                                        onClick={() => toggleCard(record.id)}
                                                        aria-expanded={!isCollapsed}
                                                    >
                                                        <span className="portfolio-learn-card-chevron" aria-hidden="true">
                                                            {isCollapsed ? <FiChevronRight /> : <FiChevronDown />}
                                                        </span>
                                                        <span className="portfolio-learn-card-id">
                                                            <strong>{record.goal}</strong>
                                                            <span>
                                                                {dateText}
                                                                {" · "}
                                                                {t(copy.savedCount(record.phases?.length || 0))}
                                                            </span>
                                                        </span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="portfolio-learn-saved-delete"
                                                        onClick={() => handleDeleteSaved(record.id)}
                                                        aria-label={t(copy.delete)}
                                                        title={t(copy.delete)}
                                                    >
                                                        <FiTrash2 />
                                                    </button>
                                                </div>

                                                {!isCollapsed && (
                                                    <RoadmapBody
                                                        roadmap={{ summary: record.summary, phases: record.phases }}
                                                        rawReply={record.rawReply}
                                                        language={language}
                                                    />
                                                )}
                                            </article>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </section>

            <PageFooter language={language}>
                <span>{pick(aboutContent.name, language)}</span> - {t(copy.footer)}
            </PageFooter>
        </div>
    );
};

export default LearningPath;
